import { useEffect, useRef, useState } from "react"
import { landingCopy } from "../landingCopy"
import { useLandingLanguage } from "../useLandingLanguage"

const slotVars = { "--slot": "min(569px, calc(100vh - 144px))" }

function VisualFrame({ children }) {
  return (
    <div className="w-full overflow-hidden rounded-[24px] bg-white shadow-[0_0_0_1px_rgba(168,168,168,0.3),0_1px_2px_rgba(0,0,0,0.06)]">
      {children}
    </div>
  )
}

function OriginBody({ body }) {
  const textClass = "text-[16px] font-normal leading-6 text-[#4D4D4D]"

  if (typeof body === "string") {
    return <p className={textClass}>{body}</p>
  }

  return (
    <div className={`flex flex-col gap-6 ${textClass}`}>
      <p>{body.intro}</p>
      <p>{body.lead}</p>
      <ul className="m-0 flex list-disc flex-col gap-6 pl-5">
        {body.items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
      <p>{body.close}</p>
    </div>
  )
}

function OriginVisual({ index }) {
  const language = useLandingLanguage()
  const origin = landingCopy(language, "origin")
  const point = origin.points[index]

  return (
    <VisualFrame>
      <div className="flex flex-col gap-3 p-6">
        <p className="text-balance text-[24px] font-bold leading-8 tracking-[-0.04em] text-[#171717]">
          {point.figure}
        </p>
        <OriginBody body={point.body} />
      </div>
    </VisualFrame>
  )
}

function StepCopy({ point, active }) {
  return (
    <div className="flex max-w-full flex-col items-start gap-2 py-6">
      <h3
        className={`text-[24px] font-normal leading-8 tracking-[-0.04em] transition-colors duration-300 ${
          active ? "text-[#171717]" : "text-[#4D4D4D]"
        }`}
      >
        {point.label}
      </h3>
      <p className="max-w-[40ch] text-pretty text-[16px] font-normal leading-6 text-[#4D4D4D]">{point.summary}</p>
    </div>
  )
}

export default function OriginSection() {
  const language = useLandingLanguage()
  const origin = landingCopy(language, "origin")
  const stepsRef = useRef([])
  const panelRef = useRef(null)
  const [active, setActive] = useState(0)

  useEffect(() => {
    const motion = window.matchMedia("(prefers-reduced-motion: no-preference)")
    const wide = window.matchMedia("(min-width: 1024px)")
    let frame = 0

    const update = () => {
      if (!motion.matches || !wide.matches) return
      const panel = panelRef.current
      if (!panel) return
      const box = panel.getBoundingClientRect()
      const line = box.top + 32
      let next = 0
      let seen = false
      stepsRef.current.forEach((el, index) => {
        if (!el) return
        const rect = el.getBoundingClientRect()
        if (rect.top <= line && rect.bottom > line) {
          next = index
          seen = true
        }
      })
      if (!seen) {
        const last = stepsRef.current[3 - 1]
        if (last && last.getBoundingClientRect().bottom <= line) {
          next = 3 - 1
        }
      }
      setActive((current) => (current === next ? current : next))
    }

    const onScroll = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(update)
    }

    update()
    window.addEventListener("scroll", onScroll, { passive: true })
    window.addEventListener("resize", onScroll)
    motion.addEventListener("change", onScroll)
    wide.addEventListener("change", onScroll)

    return () => {
      cancelAnimationFrame(frame)
      window.removeEventListener("scroll", onScroll)
      window.removeEventListener("resize", onScroll)
      motion.removeEventListener("change", onScroll)
      wide.removeEventListener("change", onScroll)
    }
  }, [])

  return (
    <section className="ml-[calc(50vw-50%-1.5rem)] flex w-full flex-col items-start gap-16 lg:gap-[72px]">
      <div className="grid w-full grid-cols-1 items-end gap-6 lg:grid-cols-12 lg:gap-x-6 lg:gap-y-3">
        <h2 className="text-balance text-[40px] font-bold leading-[48px] tracking-[-0.04em] text-[#171717] max-md:text-[28px] max-md:leading-8 lg:col-span-6 lg:col-start-1">
          {origin.title}
        </h2>
        <p className="max-w-[44ch] text-pretty text-[16px] font-normal leading-6 text-[#4D4D4D] lg:col-span-5 lg:col-start-8 lg:text-[18px] lg:leading-7">
          {origin.lead}
        </p>
      </div>

      <div className="flex w-full flex-col gap-16 motion-safe:lg:hidden">
        {origin.points.map((point, index) => (
          <div key={index} className="flex w-full flex-col items-start gap-6">
            <StepCopy point={point} active />
            <div className="w-full max-w-[560px]">
              <OriginVisual index={index} />
            </div>
          </div>
        ))}
      </div>

      <div
        className="mx-auto hidden w-max max-w-full motion-safe:lg:flex"
        style={slotVars}
      >
        <div className="flex w-[40ch] shrink-0 flex-col">
          {origin.points.map((point, index) => (
            <div
              key={index}
              ref={(node) => {
                stepsRef.current[index] = node
              }}
              className="h-[var(--slot)]"
            >
              <div className="sticky" style={{ top: "max(96px, calc(50vh - var(--slot) / 2))" }}>
                <StepCopy point={point} active={active === index} />
              </div>
            </div>
          ))}
        </div>
        <div className="relative ml-6 w-[560px] max-w-[calc(100%-40ch-1.5rem)] shrink-0">
          <div
            ref={panelRef}
            className="sticky flex w-full items-start pt-6"
            style={{ top: "max(96px, calc(50vh - var(--slot) / 2))" }}
          >
            <div className="grid w-full max-w-[560px]">
              {origin.points.map((point, index) => (
                <div
                  key={index}
                  aria-hidden={active !== index}
                  className={`col-start-1 row-start-1 transition-opacity duration-300 ${
                    active === index
                      ? "visible opacity-100"
                      : "pointer-events-none invisible h-0 overflow-hidden opacity-0"
                  }`}
                >
                  <OriginVisual index={index} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
